import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { Capacitor } from '@capacitor/core';
import EposPlugin from 'capacitor-epos';
import StarXpandPlugin from 'capacitor-starxpand';
import { ReactNode, createContext, useContext, useEffect, useRef, useState } from 'react';
import { LANG_TEXT } from '../../constants/language';
import { checkBluetoothState, requestBluetoothPermission } from '../../utils/bluetoothPermission';

export type PrinterBrand = 'star' | 'epson';

export interface PrinterConfig {
  brand: PrinterBrand | null;
  target: string;
  connectedAt?: number;
}

export interface UsePrinterConnectionState {
  printerBrand: PrinterBrand | null;
  printerTarget: string | null;
  isConnected: boolean;
  isConnecting: boolean;
  bluetoothEnabled: boolean;
  permissionDenied: boolean;
  connectionError: string | null;
  isUnsupported: boolean;
}

const STORAGE_KEYS = {
  PRINTER_BRAND: 'printer_brand_v2',
  PRINTER_TARGET: 'printer_target_v2',
  PRINTER_CONNECTED: 'printer_connected_v2',
};

const RETRY_CONFIG = {
  maxRetries: 5,
  baseDelayMs: 1000,
  btCheckIntervalMs: 5000,
};

interface PrinterContextValue extends UsePrinterConnectionState {
  checkBluetoothStatus: () => Promise<boolean>;
  requestBluetoothPermission: () => Promise<boolean>;
  loadSavedPrinter: () => Promise<boolean>;
  ensureInitialized: () => Promise<void>;
  connectPrinter: (brand: PrinterBrand, target: string, ipAddress?: string, skipBluetoothCheck?: boolean) => Promise<boolean>;
  disconnectPrinter: () => Promise<void>;
  removePrinter: () => Promise<void>;
  savePrinterConfig: (brand: PrinterBrand, target: string, connected: boolean) => void;
  markSuccessfulPrint: () => void;
}

const defaultValue: PrinterContextValue = {
  printerBrand: null,
  printerTarget: null,
  isConnected: false,
  isConnecting: false,
  bluetoothEnabled: true,
  permissionDenied: false,
  connectionError: null,
  isUnsupported: false,
  checkBluetoothStatus: async () => true,
  requestBluetoothPermission: async () => false,
  loadSavedPrinter: async () => false,
  ensureInitialized: async () => {},
  connectPrinter: async (brand: PrinterBrand, target: string, ipAddress?: string, skipBluetoothCheck?: boolean) => false,
  disconnectPrinter: async () => {},
  removePrinter: async () => {},
  savePrinterConfig: () => {},
  markSuccessfulPrint: () => {},
};

const PrinterContext = createContext<PrinterContextValue>(defaultValue);

export const PrinterProvider = ({ children }: { children: ReactNode }) => {
  const [printerBrand, setPrinterBrand] = useState<PrinterBrand | null>(null);
  const [printerTarget, setPrinterTarget] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [isConnecting, setIsConnecting] = useState<boolean>(false);
  const [bluetoothEnabled, setBluetoothEnabled] = useState<boolean>(false);
  const [permissionDenied, setPermissionDenied] = useState<boolean>(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [isUnsupported, setIsUnsupported] = useState<boolean>(false);

  const btCheckIntervalRef = useRef<any>(null);
  const connectionHealthCheckRef = useRef<any>(null);
  const resumeListenerRef = useRef<PluginListenerHandle | null>(null);
  const initializedRef = useRef(false);
  const initResolveRef = useRef<(() => void) | null>(null);
  const initPromiseRef = useRef<Promise<void>>(
    new Promise((resolve) => {
      initResolveRef.current = resolve;
    })
  );
  const lastSuccessfulPrintRef = useRef<number>(0);
  const connectLockRef = useRef<boolean>(false);
  const reconnectRef = useRef<(() => Promise<void>) | null>(null);
  // Mirror of bluetoothEnabled that is always up-to-date inside async closures
  // (React state updates are batched and may be stale when read synchronously).
  const bluetoothEnabledRef = useRef<boolean>(false);

  const acquireConnectLock = async (timeoutMs = 10000): Promise<boolean> => {
    const start = Date.now();
    while (connectLockRef.current) {
      if (Date.now() - start > timeoutMs) return false;
      await new Promise((res) => setTimeout(res, 100));
    }
    connectLockRef.current = true;
    return true;
  };

  const releaseConnectLock = () => {
    connectLockRef.current = false;
  };

  const requestBluetoothPermissionHandler = async (): Promise<boolean> => {
    try {
      if (Capacitor.getPlatform() === 'web') {
        setBluetoothEnabled(false);
        setPermissionDenied(false);
        setIsUnsupported(true);
        return false;
      }
      const permissionResult = await requestBluetoothPermission();

      if (permissionResult.granted) {
        try {
          const bt = await checkBluetoothState().catch(() => ({ enabled: false, state: 'unknown' }));
          setBluetoothEnabled(!!bt.enabled);
          bluetoothEnabledRef.current = !!bt.enabled;
          setPermissionDenied(false);
          setIsUnsupported(false);
          return !!bt.enabled;
        } catch (e) {
          setBluetoothEnabled(false);
          setPermissionDenied(false);
          return false;
        }
      } else {
        setBluetoothEnabled(false);

        if (permissionResult.state === 'denied') {
          setPermissionDenied(true);
        } else if (permissionResult.state === 'restricted') {
          setIsUnsupported(true);
        } else {
          setPermissionDenied(false);
        }

        return false;
      }
    } catch (error: any) {
      setBluetoothEnabled(false);
      setPermissionDenied(true);
      return false;
    }
  };

  const checkBluetoothStatus = async (): Promise<boolean> => {
    try {
      if (Capacitor.getPlatform() === 'web') {
        setBluetoothEnabled(false);
        setPermissionDenied(false);
        setIsUnsupported(true);
        return false;
      }

      // On iOS/Android, we assume Bluetooth is available
      // Actual permission will be requested when user scans for printers
      // This avoids triggering discovery calls during initialization
      const btState = await checkBluetoothState();

      setBluetoothEnabled(btState.enabled);
      bluetoothEnabledRef.current = btState.enabled;
      setPermissionDenied(btState.state === 'denied');
      setIsUnsupported(btState.state === 'unsupported');

      return btState.enabled;
    } catch (error: any) {
      const errorMsg = (error?.message || '').toLowerCase();
      if (errorMsg.includes('permission') || errorMsg.includes('unauthorized')) {
        setPermissionDenied(true);
        setBluetoothEnabled(false);
        return false;
      }
      if (errorMsg.includes('unsupported') || errorMsg.includes('not available')) {
        setIsUnsupported(true);
        setBluetoothEnabled(false);
        return false;
      }
      // Default to enabled on native platforms - permission will be checked on scan
      const defaultEnabled = Capacitor.getPlatform() !== 'web';
      setBluetoothEnabled(defaultEnabled);
      bluetoothEnabledRef.current = defaultEnabled;
      return defaultEnabled;
    }
  };

  const loadSavedPrinter = async (): Promise<boolean> => {
    try {
      const savedBrand = localStorage.getItem(STORAGE_KEYS.PRINTER_BRAND) as PrinterBrand | null;
      const savedTarget = localStorage.getItem(STORAGE_KEYS.PRINTER_TARGET);

      if (!savedBrand || !savedTarget) return false;

      const normalizedBrand = (savedBrand as string).toLowerCase() as PrinterBrand;
      setPrinterBrand(normalizedBrand);
      setPrinterTarget(savedTarget);

      // On iOS, killing the app leaves a stale MFi/iAP2 session open inside the
      // native EPOS/Star SDK. Calling connectPrinter without first releasing that
      // stale handle causes every retry to fail and the user thinks the printer is
      // gone. Force-disconnect first so the SDK starts from a clean state.
      if (Capacitor.getPlatform() !== 'web') {
        try {
          const plugin = normalizedBrand === 'star' ? StarXpandPlugin : EposPlugin;
          await plugin.disconnectPrinter();
        } catch (_) {}
        // Give iOS ~600 ms to fully release the BT session before we reconnect.
        await new Promise((res) => setTimeout(res, 600));
      }

      // Get a fresh BT state so connectPrinter doesn't rely on potentially-stale
      // React state that hasn't re-rendered yet.
      const btEnabled = await checkBluetoothStatus().catch(() => bluetoothEnabledRef.current);

      try {
        // skipBluetoothCheck=true because we already verified BT state above.
        // This avoids re-triggering the permission dialog during a silent reconnect.
        await connectPrinter(normalizedBrand, savedTarget, undefined, !btEnabled);
        return true;
      } catch (error: any) {
        setIsConnected(false);
        savePrinterConfig(normalizedBrand, savedTarget, false);
        return false;
      }
    } catch (error: any) {
      return false;
    }
  };

  const ensureInitialized = async (): Promise<void> => {
    if (initializedRef.current) return;
    await initPromiseRef.current;
  };

  const connectPrinter = async (
    brand: PrinterBrand,
    target: string,
    ipAddress?: string,
    skipBluetoothCheck = false,
    retries = RETRY_CONFIG.maxRetries
  ): Promise<boolean> => {
    setConnectionError(null);

    // Acquire connect lock to avoid concurrent connect/disconnect operations
    const lockAcquired = await acquireConnectLock();
    if (!lockAcquired) {
      setConnectionError(LANG_TEXT.FAILED_TO_CONNECT_PRINTER);
      return false;
    }

    if (!skipBluetoothCheck) {
      // Use the ref mirror so we always get the current value rather than
      // a potentially-stale closure from the last render cycle.
      let btReady = bluetoothEnabledRef.current || bluetoothEnabled;
      if (!btReady) {
        const permissionGranted = await requestBluetoothPermissionHandler();
        if (!permissionGranted) {
          setConnectionError(LANG_TEXT.BLUETOOTH_PERMISSION_REQUIRED);
          releaseConnectLock();
          return false;
        }
        // Permission handler confirmed BT is enabled; React state may not
        // have re-rendered yet, so track readiness via a local variable.
        btReady = true;
      }

      if (!btReady) {
        setConnectionError(LANG_TEXT.BLUETOOTH_DISABLED);
        releaseConnectLock();
        return false;
      }
    }

    if (!target && !ipAddress) {
      setConnectionError(LANG_TEXT.INVALID_PRINTER_TARGET);
      releaseConnectLock();
      return false;
    }

    if (!skipBluetoothCheck) {
      setIsConnecting(true);
    }

    const mapConnectionError = (msg: string | undefined | null) => {
      const errorMsg = (msg || '').toLowerCase();
      if (!errorMsg) return LANG_TEXT.UNKNOWN_CONNECTION_ERROR;
      if (errorMsg.includes('permission') || errorMsg.includes('unauthorized')) return LANG_TEXT.BLUETOOTH_PERMISSION_DENIED;
      if (errorMsg.includes('unsupported') || errorMsg.includes('not available')) return LANG_TEXT.BLUETOOTH_NOT_SUPPORTED;
      if (errorMsg.includes('timeout') || errorMsg.includes('not found')) return LANG_TEXT.FAILED_TO_CONNECT_PRINTER;
      if (errorMsg.includes('offline') || errorMsg.includes('disconnected')) return LANG_TEXT.FAILED_TO_CONNECT_PRINTER;
      return LANG_TEXT.UNKNOWN_CONNECTION_ERROR;
    };

    try {
      const plugin = brand === 'star' ? StarXpandPlugin : EposPlugin;
      let lastError: any;

      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          await plugin.connectPrinter({
            target,
            identifier: target,
            ipAddress,
          });
          if (!skipBluetoothCheck) {
            setPrinterBrand(brand);
            setPrinterTarget(ipAddress || target);
            setIsConnected(true);
            savePrinterConfig(brand, ipAddress || target, true);
          }
          return true;
        } catch (error: any) {
          lastError = error;
          if (attempt < retries) {
            const delayMs = RETRY_CONFIG.baseDelayMs * attempt;
            await new Promise((res) => setTimeout(res, delayMs));
          }
        }
      }

      if (!skipBluetoothCheck) {
        setIsConnected(false);
        savePrinterConfig(brand, ipAddress || target, false);

        try {
          const searchResult: any = await plugin.searchPrinters().catch(() => null);
          const discovered = (searchResult && (searchResult.printers || [])) || [];

          if (Array.isArray(discovered) && discovered.length > 0) {
            const normalizeAddr = (p: any) => p.target || p.identifier || p.address || '';
            const exact = discovered.find((p: any) => normalizeAddr(p) === target);

            if (exact) {
              const addr = normalizeAddr(exact);
              if (addr) {
                try {
                  await plugin.connectPrinter({
                    target: addr,
                    identifier: addr,
                    ipAddress: addr,
                  });
                  setPrinterBrand(brand);
                  setPrinterTarget(addr);
                  setIsConnected(true);
                  savePrinterConfig(brand, addr, true);
                  return true;
                } catch (err: any) {}
              }
            }
          }
        } catch {}
      }

      const finalError = lastError || new Error('Failed to connect to printer');
      const mapped = mapConnectionError(finalError?.message);
      if (mapped === LANG_TEXT.BLUETOOTH_PERMISSION_DENIED) setPermissionDenied(true);
      if (mapped === LANG_TEXT.BLUETOOTH_NOT_SUPPORTED) setIsUnsupported(true);
      setConnectionError(mapped);
      return false;
    } catch (error: any) {
      setIsConnected(false);
      const mapped = mapConnectionError(error?.message);
      setConnectionError(mapped);
      return false;
    } finally {
      setIsConnecting(false);
      releaseConnectLock();
    }
  };

  const disconnectPrinter = async (): Promise<void> => {
    if (!printerBrand) return;

    // Wait for any active connect operation and acquire lock
    const lockAcquired = await acquireConnectLock().catch(() => false);
    if (!lockAcquired) {
      // If we cannot acquire lock, still attempt a best-effort disconnect
      try {
        const plugin = printerBrand === 'star' ? StarXpandPlugin : EposPlugin;
        await plugin.disconnectPrinter();
      } catch (_) {}
      setIsConnected(false);
      return;
    }

    try {
      const plugin = printerBrand === 'star' ? StarXpandPlugin : EposPlugin;
      await plugin.disconnectPrinter();
      setIsConnected(false);
    } catch (error: any) {
      setIsConnected(false);
    } finally {
      releaseConnectLock();
    }
  };

  const removePrinter = async (): Promise<void> => {
    try {
      await disconnectPrinter();
      setPrinterBrand(null);
      setPrinterTarget(null);
      setIsConnected(false);
      localStorage.removeItem(STORAGE_KEYS.PRINTER_BRAND);
      localStorage.removeItem(STORAGE_KEYS.PRINTER_TARGET);
      localStorage.removeItem(STORAGE_KEYS.PRINTER_CONNECTED);
    } catch (error: any) {
      throw error;
    }
  };

  const savePrinterConfig = (brand: PrinterBrand, target: string, connected: boolean): void => {
    try {
      const normalized = (brand as string).toLowerCase();
      localStorage.setItem(STORAGE_KEYS.PRINTER_BRAND, normalized);
      localStorage.setItem(STORAGE_KEYS.PRINTER_TARGET, target);
      localStorage.setItem(STORAGE_KEYS.PRINTER_CONNECTED, connected ? 'true' : 'false');
    } catch (error: any) {}
  };

  const markSuccessfulPrint = (): void => {
    lastSuccessfulPrintRef.current = Date.now();
  };

  useEffect(() => {
    const initialize = async () => {
      try {
        const btNow = await checkBluetoothStatus().catch(() => false);
        if (!btNow && Capacitor.getPlatform() !== 'web') {
          const perm = await requestBluetoothPermission().catch(() => ({ granted: false, state: 'not_determined' }));
          if (perm.granted) {
            await checkBluetoothStatus().catch(() => false);
          } else {
            setBluetoothEnabled(false);
            setPermissionDenied(perm.state === 'denied');
            setIsUnsupported(perm.state === 'unsupported');
          }
        }
      } catch (e) {}

      await loadSavedPrinter();
      initializedRef.current = true;
      if (initResolveRef.current) initResolveRef.current();
    };

    initialize();

    return () => {
      if (btCheckIntervalRef.current) clearInterval(btCheckIntervalRef.current);
      if (connectionHealthCheckRef.current) clearInterval(connectionHealthCheckRef.current);
    };
  }, []);

  // Poll OS Bluetooth state periodically to detect external disconnects
  useEffect(() => {
    // Clear any existing interval
    if (btCheckIntervalRef.current) {
      clearInterval(btCheckIntervalRef.current);
      btCheckIntervalRef.current = null;
    }

    const startPolling = () => {
      btCheckIntervalRef.current = setInterval(async () => {
        try {
          const enabled = await checkBluetoothStatus().catch(() => false);
          // If OS reports disabled while app thought enabled, update state and disconnect
          if (!enabled && bluetoothEnabled) {
            setBluetoothEnabled(false);
            setIsConnected(false);
            setConnectionError('BLUETOOTH_DISABLED');
          } else if (enabled && !bluetoothEnabled) {
            setBluetoothEnabled(true);
            setConnectionError(null);
          }
        } catch (e) {
          // ignore polling errors
        }
      }, RETRY_CONFIG.btCheckIntervalMs);
    };

    // Start polling on native platforms only
    if (Capacitor.getPlatform() !== 'web') startPolling();

    return () => {
      if (btCheckIntervalRef.current) {
        clearInterval(btCheckIntervalRef.current);
        btCheckIntervalRef.current = null;
      }
    };
  }, [bluetoothEnabled]);

  useEffect(() => {
    if (connectionHealthCheckRef.current) {
      clearInterval(connectionHealthCheckRef.current);
      connectionHealthCheckRef.current = null;
    }

    const savedBrand = (localStorage.getItem(STORAGE_KEYS.PRINTER_BRAND) as PrinterBrand | null) || printerBrand;
    const savedTarget = localStorage.getItem(STORAGE_KEYS.PRINTER_TARGET) || printerTarget;

    if (!savedBrand || !savedTarget || Capacitor.getPlatform() === 'web') {
      return;
    }

    const checkConnectionHealth = async () => {
      if (isConnecting || !bluetoothEnabled) {
        return;
      }

      try {
        const plugin = savedBrand === 'star' ? StarXpandPlugin : EposPlugin;

        try {
          if (savedBrand === 'star' && typeof plugin.getStatus === 'function') {
            await plugin.getStatus();
            if (!isConnected) {
              setIsConnected(true);
            }
            lastSuccessfulPrintRef.current = Date.now();
          } else if (savedBrand === 'epson') {
            if (isConnected) {
              await plugin.getStatus();
              lastSuccessfulPrintRef.current = Date.now();
            }
          }
        } catch (statusError: any) {
          if (isConnected) {
            setIsConnected(false);

            setTimeout(async () => {
              if (!isConnecting && bluetoothEnabled) {
                try {
                  await connectPrinter(savedBrand, savedTarget);
                } catch (_) {}
              }
            }, 3000);
          }
        }
      } catch (e) {}
    };

    connectionHealthCheckRef.current = setInterval(checkConnectionHealth, 15000);

    return () => {
      if (connectionHealthCheckRef.current) {
        clearInterval(connectionHealthCheckRef.current);
        connectionHealthCheckRef.current = null;
      }
    };
  }, [isConnected, isConnecting, bluetoothEnabled, printerBrand, printerTarget]);

  // Keep a stable ref for all values needed by the resume handler so the
  // listener is registered only once (no stale-closure re-registrations).
  const resumeStateRef = useRef({ isConnected, isConnecting });
  useEffect(() => {
    resumeStateRef.current = { isConnected, isConnecting };
  }, [isConnected, isConnecting]);

  useEffect(() => {
    const handleResume = async () => {
      try {
        const savedBrand = (localStorage.getItem(STORAGE_KEYS.PRINTER_BRAND) as PrinterBrand | null) || null;
        const savedTarget = localStorage.getItem(STORAGE_KEYS.PRINTER_TARGET) || null;

        if (!savedBrand || !savedTarget) return;

        const btNow = await checkBluetoothStatus().catch(() => bluetoothEnabledRef.current);
        if (!btNow) return;

        const { isConnecting: resumeConnecting } = resumeStateRef.current;
        if (resumeConnecting) return;

        try {
          const plugin = savedBrand === 'star' ? StarXpandPlugin : EposPlugin;

          // Always try getStatus first. iOS terminates MFi/iAP2 sessions when the
          // app is backgrounded; if getStatus throws the session is dead.
          try {
            await plugin.getStatus();
            // Status OK — connection survived the background cycle, nothing to do.
            return;
          } catch (_statusErr) {
            // Session is dead — mark disconnected and force-clean the native state.
            setIsConnected(false);
          }

          // Force-release the stale native session before reconnecting
          // (same reason as loadSavedPrinter: iOS needs the old handle released).
          try {
            await plugin.disconnectPrinter();
          } catch (_) {}
          await new Promise((res) => setTimeout(res, 600));

          await connectPrinter(savedBrand, savedTarget).catch(() => {});
        } catch (e) {
          // swallow errors
        }
      } catch (e) {
        // swallow errors to avoid unhandled rejections
      }
    };

    App.addListener('resume', handleResume)
      .then((handle) => {
        resumeListenerRef.current = handle;
      })
      .catch(() => {});

    return () => {
      try {
        resumeListenerRef.current && resumeListenerRef.current.remove();
      } catch (e) {}
    };
    // Register once — state is read through resumeStateRef to avoid re-registrations.
  }, []);

  // Keep reconnect ref always up-to-date so event listeners can use it
  // without stale closures.
  reconnectRef.current = async () => {
    if (isConnecting || isConnected) return;
    const savedBrand = localStorage.getItem(STORAGE_KEYS.PRINTER_BRAND) as PrinterBrand | null;
    const savedTarget = localStorage.getItem(STORAGE_KEYS.PRINTER_TARGET);
    if (!savedBrand || !savedTarget) return;
    await connectPrinter(savedBrand, savedTarget).catch(() => {});
  };

  // Listen for native disconnect events (safely).
  useEffect(() => {
    if (Capacitor.getPlatform() === 'web') return;

    let listenerHandle: PluginListenerHandle | null = null;
    let handlePromise: Promise<PluginListenerHandle> | null = null;

    try {
      // Guard against missing plugin or missing API (calling undefined would throw)
      if (EposPlugin && typeof (EposPlugin as any).addListener === 'function') {
        handlePromise = (EposPlugin as any)
          .addListener('onConnectionStateChange', (data: { connected: boolean; reason: string }) => {
            if (data?.connected === false) {
              setIsConnected(false);

              // When the app returns to foreground, iOS has already terminated
              // the MFi/iAP2 session. Trigger an automatic reconnect.
              if (data?.reason === 'app_foreground') {
                setTimeout(() => {
                  reconnectRef.current?.();
                }, 1500);
              }
            }
          })
          .then((h: PluginListenerHandle) => {
            listenerHandle = h;
            return h;
          })
          .catch((e: any) => {
            console.warn('EposPlugin.addListener failed', e);
            return null as any;
          });
      }
    } catch (e) {
      console.warn('Failed to attach onConnectionStateChange listener', e);
    }

    return () => {
      try {
        if (listenerHandle && typeof (listenerHandle as any).remove === 'function') {
          (listenerHandle as any).remove().catch(() => {});
        } else if (handlePromise) {
          handlePromise
            .then((h) => {
              if (h && typeof (h as any).remove === 'function') (h as any).remove().catch(() => {});
            })
            .catch(() => {});
        }
      } catch (e) {}
    };
  }, []);

  const value: PrinterContextValue = {
    printerBrand,
    printerTarget,
    isConnected,
    isConnecting,
    bluetoothEnabled,
    permissionDenied,
    connectionError,
    isUnsupported,

    checkBluetoothStatus,
    requestBluetoothPermission: requestBluetoothPermissionHandler,
    loadSavedPrinter,
    ensureInitialized,
    connectPrinter,
    disconnectPrinter,
    removePrinter,
    savePrinterConfig,
    markSuccessfulPrint,
  };

  return <PrinterContext.Provider value={value}>{children}</PrinterContext.Provider>;
};

export const usePrinterContext = () => useContext(PrinterContext);

export default PrinterProvider;
